"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { readPoem } from "@/lib/tool-kit/poetry/pronounce";
import {
  analyzeRhyme,
  type FormId,
  type SchemeLine,
  type SlantKind,
} from "@/lib/tool-kit/poetry/rhyme";
import { POETRY_READING_STRINGS } from "@/lib/tool-kit/poetry/strings";
import { usePoetryDictionary } from "@/lib/tool-kit/poetry/use-dictionary";
import { arpabetToIpa } from "@/lib/tool-kit/vocabulary/ipa";

const SONNET = `Shall I compare thee to a summer’s day?
Thou art more lovely and more temperate:
Rough winds do shake the darling buds of May,
And summer’s lease hath all too short a date:
Sometime too hot the eye of heaven shines,
And often is his gold complexion dimm’d,
And every fair from fair sometime declines,
By chance, or nature’s changing course untrimm’d:
But thy eternal summer shall not fade,
Nor lose possession of that fair thou ow’st,
Nor shall death brag thou wander’st in his shade,
When in eternal lines to time thou grow’st,
So long as men can breathe, or eyes can see,
So long lives this, and this gives life to thee.`;

const LEAR = `There was an Old Man with a beard,
Who said, “It is just as I feared!
Two Owls and a Hen,
Four Larks and a Wren,
Have all built their nests in my beard!”`;

const POPE = `A little learning is a dangerous thing;
Drink deep, or taste not the Pierian spring:
There shallow draughts intoxicate the brain,
And drinking largely sobers us again.`;

/** English strings. A translated content file overrides these through `ui`. */
export const RHYME_SCHEME_ANALYZER_STRINGS = {
  ...POETRY_READING_STRINGS,
  textLabel:
    "Your poem — one line of verse per line, blank line between stanzas",
  textPlaceholder: POPE,
  samplesLabel: "Try a sample:",
  sampleSonnet: "Shakespeare, Sonnet 18",
  sampleLear: "Lear, an Old Man with a beard",
  samplePope: "Pope, An Essay on Criticism",
  sampleSonnetText: SONNET,
  sampleLearText: LEAR,
  samplePopeText: POPE,
  sampleCredit:
    "Samples are public-domain texts: Shakespeare’s Sonnets (1609), Edward Lear’s A Book of Nonsense (1846), Alexander Pope’s An Essay on Criticism (1711).",
  clear: "Clear",
  slantLabel: "Count slant rhymes as rhymes (assonance and consonance)",
  identicalLabel: "Count a repeated end word as a rhyme (a refrain)",
  emptyPrompt: "Type or paste a poem to letter its rhyme scheme.",
  heading: "Rhyme scheme",
  schemeLabel: "Scheme",
  formFound: "This is the pattern of {form}.",
  forms: {
    couplets: "rhyming couplets",
    alternate: "an alternate-rhyme quatrain (ABAB)",
    enclosed: "an enclosed-rhyme quatrain (ABBA)",
    ballad: "a ballad stanza (ABCB)",
    monorhyme: "monorhyme (every line rhymes)",
    limerick: "a limerick (AABBA)",
    shakespearean: "a Shakespearean sonnet (ABAB CDCD EFEF GG)",
    petrarchan: "a Petrarchan sonnet (ABBAABBA, then a sestet)",
    spenserianSonnet: "a Spenserian sonnet (ABAB BCBC CDCD EE)",
    rhymeRoyal: "rhyme royal (ABABBCC)",
    ottavaRima: "ottava rima (ABABABCC)",
    spenserianStanza: "a Spenserian stanza (ABABBCBCC)",
    terzaRima: "terza rima (ABA BCB CDC…)",
    villanelle: "a villanelle (ABA ABA ABA ABA ABA ABAA)",
    tercets: "rhyming tercets (AAA)",
  },
  summary: "{sounds} in {lines}.",
  soundCount: "one:{count} rhyme sound|other:{count} rhyme sounds",
  lineCount: "one:{count} line|other:{count} lines",
  unrhymed:
    "one:{count} line has no rhyme partner.|other:{count} lines have no rhyme partner.",
  tableHeading: "Line by line",
  colLine: "Line",
  colLetter: "Letter",
  colEnding: "Ending word",
  colSound: "Rhyming sound",
  colLink: "Rhymes with",
  colType: "Type",
  linkTo: "line {number} ({word})",
  noLink: "—",
  typeNew: "first of its sound",
  typePerfectGender: "perfect, {gender}",
  typeIdentical: "same word",
  typeSlant: "slant ({kind})",
  typeGuess: "perfect (guessed from spelling)",
  typeNone: "no ending word",
  genderMasculine: "masculine",
  genderFeminine: "feminine",
  genderTriple: "triple",
  kinds: {
    assonance: "assonance",
    consonance: "consonance",
    "final-vowel": "final vowel",
  },
  eyeNote: "Spelled like line {number} but pronounced differently (eye rhyme).",
  nearHeading: "Near rhymes not counted",
  nearHint:
    "These pairs share a vowel or closing consonants but are not perfect rhymes. Tick the slant box to letter them as rhymes.",
  nearItem:
    "Line {first} “{firstWord}” and line {second} “{secondWord}” — {kind}",
  copyScheme: "Copy scheme",
  copied: "Copied!",
  methodNote:
    "Rhymes are judged by modern General American pronunciation from the CMU Pronouncing Dictionary, so rhymes that have drifted apart since the poem was written (love and prove, wind and find) may not be found.",
} as const;

type Strings = typeof RHYME_SCHEME_ANALYZER_STRINGS;

const ID = "rhyme-scheme";

function ipaOf(tail: readonly string[] | null): string {
  if (!tail) {
    return "";
  }
  return arpabetToIpa(tail.join(" "), { stressMarks: false }) ?? "";
}

function genderOf(syllables: number, strings: Strings): string {
  if (syllables <= 1) {
    return strings.genderMasculine;
  }
  return syllables === 2 ? strings.genderFeminine : strings.genderTriple;
}

function typeText(entry: SchemeLine, strings: Strings): string {
  if (!entry.end) {
    return strings.typeNone;
  }
  const match = entry.match;
  if (!match) {
    return strings.typeNew;
  }
  const { link } = match;
  if (link.relation === "identical") {
    return strings.typeIdentical;
  }
  if (link.relation === "slant" && link.kind) {
    return t(strings.typeSlant, { kind: strings.kinds[link.kind] });
  }
  if (link.guess) {
    return strings.typeGuess;
  }
  return t(strings.typePerfectGender, {
    gender: genderOf(entry.end.tailSyllables, strings),
  });
}

export function SchemeTable({
  lines,
  strings,
}: {
  lines: readonly SchemeLine[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <div className="max-h-[520px] overflow-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            {[
              strings.colLine,
              strings.colLetter,
              strings.colEnding,
              strings.colSound,
              strings.colLink,
              strings.colType,
            ].map((label) => (
              <th className={TH} key={label} scope="col">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map((entry, index) => (
            <tr key={entry.line.number}>
              <td className={TD}>{fmt(index + 1)}</td>
              <td className={`${TD} font-semibold`}>{entry.letter ?? "—"}</td>
              <td className={TD}>{entry.end?.word.text ?? ""}</td>
              <td className={`${TD} text-base`}>
                {ipaOf(entry.end?.tail ?? null)}
              </td>
              <td className={TD}>
                {entry.match
                  ? t(strings.linkTo, {
                      number: fmt(lines.indexOf(entry.match.line) + 1),
                      word: entry.match.line.end?.word.text ?? "",
                    })
                  : strings.noLink}
                {entry.match?.link.eye ? (
                  <span className="block text-muted-foreground text-xs">
                    {t(strings.eyeNote, {
                      number: fmt(lines.indexOf(entry.match.line) + 1),
                    })}
                  </span>
                ) : null}
              </td>
              <td className={TD}>{typeText(entry, strings)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function kindName(kind: SlantKind | null, strings: Strings): string {
  return kind ? strings.kinds[kind] : "";
}

function formText(form: FormId | null, strings: Strings): string {
  return form ? t(strings.formFound, { form: strings.forms[form] }) : "";
}

export default function RhymeSchemeAnalyzerWidget() {
  const strings = useToolUi(RHYME_SCHEME_ANALYZER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [slant, setSlant] = useState(false);
  const [identical, setIdentical] = useState(true);
  const hasText = text.trim().length > 0;
  const { lookup, status } = usePoetryDictionary(hasText);

  const analysis = useMemo(
    () =>
      hasText
        ? analyzeRhyme(readPoem(text, lookup), { slant, identical })
        : null,
    [hasText, text, lookup, slant, identical]
  );
  const samples = [
    [strings.sampleSonnet, strings.sampleSonnetText],
    [strings.sampleLear, strings.sampleLearText],
    [strings.samplePope, strings.samplePopeText],
  ] as const;

  const rhymed = analysis?.lines.filter((entry) => entry.letter !== null) ?? [];
  const alone = analysis
    ? analysis.lines.filter(
        (entry) =>
          entry.letter !== null &&
          analysis.lines.filter((other) => other.letter === entry.letter)
            .length === 1
      ).length
    : 0;
  const schemeText = analysis?.stanzas.join(" ") ?? "";

  return (
    <>
      <TextAreaField
        id={`${ID}-text`}
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={8}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className={HINT}>{strings.samplesLabel}</span>
        {samples.map(([label, body]) => (
          <button
            className={BUTTON_SECONDARY}
            key={label}
            onClick={() => setText(body)}
            type="button"
          >
            {label}
          </button>
        ))}
        <button
          className={BUTTON_SECONDARY}
          disabled={!hasText}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Checkbox
          checked={slant}
          label={strings.slantLabel}
          onChange={setSlant}
        />
        <Checkbox
          checked={identical}
          label={strings.identicalLabel}
          onChange={setIdentical}
        />
      </div>
      {status === "loading" ? (
        <Notice>{strings.dictionaryLoading}</Notice>
      ) : null}
      {status === "failed" ? (
        <ErrorNotice>{strings.dictionaryFailed}</ErrorNotice>
      ) : null}

      {analysis && rhymed.length > 0 ? (
        <section aria-live="polite" className="space-y-5">
          <h2 className={WIDGET_H2}>{strings.heading}</h2>
          <div className={RESULT_CARD}>
            <p className="text-muted-foreground text-xs">
              {strings.schemeLabel}
            </p>
            <p className="break-words font-mono font-semibold text-2xl text-foreground tracking-wider">
              {schemeText}
            </p>
            {analysis.form ? (
              <p className="mt-2 text-foreground text-sm">
                {formText(analysis.form, strings)}
              </p>
            ) : null}
            <p className="mt-1 text-muted-foreground text-sm">
              {t(strings.summary, {
                sounds: tPlural(strings.soundCount, locale, analysis.sounds, {
                  count: fmt(analysis.sounds),
                }),
                lines: tPlural(
                  strings.lineCount,
                  locale,
                  analysis.lines.length,
                  {
                    count: fmt(analysis.lines.length),
                  }
                ),
              })}
            </p>
            {alone > 0 ? (
              <p className="mt-1 text-muted-foreground text-sm">
                {tPlural(strings.unrhymed, locale, alone, {
                  count: fmt(alone),
                })}
              </p>
            ) : null}
          </div>
          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
            <SchemeTable lines={analysis.lines} strings={strings} />
          </div>
          {analysis.nearRhymes.length > 0 ? (
            <section className="space-y-2">
              <h3 className={WIDGET_H3}>{strings.nearHeading}</h3>
              <p className={HINT}>{strings.nearHint}</p>
              <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
                {analysis.nearRhymes.map((near) => (
                  <li
                    key={`${near.first.line.number}-${near.second.line.number}`}
                  >
                    {t(strings.nearItem, {
                      first: fmt(analysis.lines.indexOf(near.first) + 1),
                      firstWord: near.first.end?.word.text ?? "",
                      second: fmt(analysis.lines.indexOf(near.second) + 1),
                      secondWord: near.second.end?.word.text ?? "",
                      kind: kindName(near.link.kind, strings),
                    })}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          <p className={HINT}>{strings.methodNote}</p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyScheme}
            text={schemeText}
          />
          <p className={HINT}>{strings.sampleCredit}</p>
        </section>
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
